import React from 'react';
interface LogoProps {
  size?: 'sm' | 'md' | 'lg';
  showText?: boolean;
  /** Use on dark surfaces — the mark inverts and the wordmark goes white. */
  tone?: 'dark' | 'light';
  className?: string;
}

/**
 * The original raster wordmark. Still exported because a few surfaces embed
 * it as an image asset (merchant widgets, storefront chrome) where an inline
 * SVG is not available.
 */
export const LOGO_SRC = 'https://cdn.magicpatterns.com/uploads/4YWxpYRVWvWhebcgYXkofj/ChatGPT_Image_Jun_14,_2026,_06_55_09_PM.png';
const NAVY = '#0B1220';
const INDIGO = '#4338CA';

/**
 * The mark: a speech bubble with a spark knocked out of it. Talab's whole
 * product is a question turning into an answer, so the mark is the ask (the
 * bubble) and the intelligence answering it (the spark) in one silhouette.
 *
 * Drawn as negative space rather than two stacked objects, so it stays a
 * single confident shape at 16px and only reveals the spark at display size.
 */
export function TalabMark({
  size = 34,
  tone = 'dark',
  className = ''
}: {
  size?: number;
  tone?: 'dark' | 'light';
  className?: string;
}) {
  const tile = tone === 'dark' ? NAVY : '#FFFFFF';
  const glyph = tone === 'dark' ? '#FFFFFF' : NAVY;
  return <svg width={size} height={size} viewBox="0 0 40 40" fill="none" role="img" aria-label="Talab" className={className} data-id="element-2794" data-mp="1nwhbouqsx2cw">
      <rect width="40" height="40" rx="11.5" fill={tile} data-id="element-2795" data-mp="1xtc91abc8wpl" />
      {/* Bubble + tail as one filled silhouette, spark cut out of it */}
      <path fillRule="evenodd" clipRule="evenodd" d="M15.2 9.5h9.6c2.06 0 3.12 0 3.99.32a5 5 0 0 1 2.99 2.99c.32.87.32 1.93.32 3.99v2.9c0 2.06 0 3.12-.32 3.99a5 5 0 0 1-2.99 2.99c-.87.32-1.93.32-3.99.32h-4.02l-5.12 3.9c-.86.65-2.1.04-2.1-1.04v-3.09c-1.1-.21-1.84-.6-2.44-1.2a5 5 0 0 1-1.1-1.88c-.32-.87-.32-1.93-.32-3.99v-2.9c0-2.06 0-3.12.32-3.99a5 5 0 0 1 2.99-2.99c.87-.32 1.93-.32 3.99-.32Zm4.8 4.1c.55 3.2 1.5 4.15 4.7 4.7-3.2.55-4.15 1.5-4.7 4.7-.55-3.2-1.5-4.15-4.7-4.7 3.2-.55 4.15-1.5 4.7-4.7Z" fill={glyph} data-id="element-2796" data-mp="1stzlsogk1nmh" />
    </svg>;
}

/**
 * The lockup. Lowercase wordmark, tight tracking — the mark carries the
 * personality, so the type stays quiet and lets it.
 */
export function Logo({
  size = 'md',
  showText = true,
  tone = 'dark',
  className = ''
}: LogoProps) {
  const marks = {
    sm: 28,
    md: 34,
    lg: 44
  };
  const type = {
    sm: 19,
    md: 23,
    lg: 30
  };
  const mark = marks[size];
  return <div className={`flex items-center gap-2.5 ${className}`} data-id="element-2797" data-mp="1wwxaucamxhad">
      <TalabMark size={mark} tone={tone} data-id="element-2798" data-mp="1wz9sspge7rnf" />
      {showText && <span className="font-display font-bold leading-none select-none" style={{
      fontSize: type[size],
      letterSpacing: '-0.035em',
      color: tone === 'dark' ? NAVY : '#FFFFFF'
    }} data-id="element-2799" data-mp="21x1jxh97r9qk">
          talab
          <span style={{
        color: tone === 'dark' ? INDIGO : '#C4B5FD'
      }} data-id="element-2800" data-mp="bjc5q9610n1y">
            .
          </span>
        </span>}
    </div>;
}